/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback } from 'react';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { OLX_TERMS_VERSION, PortalSettings, apiRequest } from './constants';
import { normalizePortalSettings } from './normalizePortalSettings';

export type PortalActions = {
  updateSettings: (updates: Partial<PortalSettings>) => Promise<void>;
  connectOlx: () => Promise<void>;
  disconnectOlx: () => Promise<void>;
  confirmOlx: () => Promise<void>;
  checkOlxPlan: () => Promise<void>;
  createCnmFeedUrl: () => Promise<string | null>;
  saveCnmLeadCredentials: (email: string, token: string) => Promise<string | null>;
};

type Deps = {
  agencyId: string | undefined;
  t: (key: string) => string;
  setSettings: React.Dispatch<React.SetStateAction<PortalSettings | null>>;
  setSaving: (v: boolean) => void;
  fetchSettings: () => Promise<void>;
};

/**
 * Optimistically applies portal settings updates and persists them, rolling back on error.
 *
 * @param deps - Portal action dependencies.
 * @param updates - The partial settings to apply.
 */
async function doUpdate(deps: Deps, updates: Partial<PortalSettings>) {
  const { agencyId, t, setSettings, setSaving, fetchSettings } = deps;
  if (!agencyId) return;
  setSettings((prev) => prev ? { ...prev, ...updates } : prev);
  setSaving(true);
  try {
    const raw = await apiRequest(
      `/portal-integration/agencies/${agencyId}/settings`, 'PUT', updates,
    );
    setSettings((prev) => prev ? normalizePortalSettings(raw, agencyId, prev) : prev);
  } catch {
    notifications.show({
      title: t('portal.notifications.erroTituloErro'),
      message: t('portal.notifications.erroSalvar'),
      type: 'error',
    });
    await fetchSettings();
  } finally {
    setSaving(false);
  }
}

/**
 * Starts the OLX OAuth connection flow, redirecting to the authorization URL.
 *
 * @param agencyId - The agency id.
 * @param t - Translation function.
 */
async function doConnect(agencyId: string | undefined, t: (k: string) => string) {
  if (!agencyId) return;
  try {
    const data = await apiRequest(
      `/portal-integration/agencies/${agencyId}/settings/olx/connect`, 'POST',
    );
    if (data?.authorization_url) window.location.href = data.authorization_url;
  } catch {
    notifications.show({
      title: t('portal.notifications.erroTituloErro'),
      message: t('portal.notifications.falhaConexaoOlx'),
      type: 'error',
    });
  }
}

/**
 * Disconnects the OLX integration and refreshes the settings.
 *
 * @param agencyId - The agency id.
 * @param t - Translation function.
 * @param fetchSettings - Callback to refresh settings after disconnect.
 */
async function doDisconnect(agencyId: string | undefined, t: (k: string) => string, fetchSettings: () => Promise<void>) {
  if (!agencyId) return;
  try {
    await apiRequest(`/portal-integration/agencies/${agencyId}/settings/olx/disconnect`, 'POST');
    await fetchSettings();
    notifications.show({
      title: t('portal.notifications.desconectadoTitulo'),
      message: t('portal.notifications.olxDesconectado'),
      type: 'success',
    });
  } catch {
    notifications.show({
      title: t('portal.notifications.erroTituloErro'),
      message: t('portal.notifications.falhaDesconectarOlx'),
      type: 'error',
    });
  }
}

/**
 * Confirms that the connected OLX account is the agency's and accepts the OLX
 * publishing terms shown in the pop-up (sent by version), then refreshes.
 *
 * @param agencyId - The agency id.
 * @param t - Translation function.
 * @param fetchSettings - Callback to refresh settings afterwards.
 */
async function doConfirmOlx(agencyId: string | undefined, t: (k: string) => string, fetchSettings: () => Promise<void>) {
  if (!agencyId) return;
  try {
    await apiRequest(
      `/portal-integration/agencies/${agencyId}/settings/olx/confirm`,
      'POST',
      { terms_version: OLX_TERMS_VERSION },
    );
    await fetchSettings();
    notifications.show({
      title: t('olx.confirmadoTitulo'),
      message: t('olx.confirmado'),
      type: 'success',
    });
  } catch {
    notifications.show({
      title: t('portal.notifications.erroTituloErro'),
      message: t('olx.falhaConfirmar'),
      type: 'error',
    });
  }
}

/**
 * Asks OLX again whether the account has a plan, then refreshes.
 *
 * @param agencyId - The agency id.
 * @param t - Translation function.
 * @param fetchSettings - Callback to refresh settings afterwards.
 */
async function doCheckOlxPlan(agencyId: string | undefined, t: (k: string) => string, fetchSettings: () => Promise<void>) {
  if (!agencyId) return;
  try {
    const plan = await apiRequest(`/portal-integration/agencies/${agencyId}/settings/olx/plan`, 'POST');
    await fetchSettings();
    const status = (plan as { status?: string } | null)?.status;
    const active = status === 'active';
    notifications.show({
      title: active ? t('olx.planoAtivoTitulo') : t('olx.planoAusenteTitulo'),
      message: active ? t('olx.planoAtivo') : t(status === 'missing' ? 'olx.planoAusente' : 'olx.planoDesconhecido'),
      type: active ? 'success' : 'error',
    });
  } catch {
    notifications.show({
      title: t('portal.notifications.erroTituloErro'),
      message: t('olx.planoDesconhecido'),
      type: 'error',
    });
  }
}

/**
 * Generates a new private Chaves na Mão feed URL. Only its hash is stored, so
 * the URL is shown once and the previous one stops working.
 *
 * @returns The URL to give Chaves na Mão, or null on failure.
 */
async function doCreateCnmFeedUrl(
  agencyId: string | undefined, t: (k: string) => string,
  setSettings: React.Dispatch<React.SetStateAction<PortalSettings | null>>,
): Promise<string | null> {
  if (!agencyId) return null;
  try {
    const res = await apiRequest(`/portal-integration/agencies/${agencyId}/settings/chaves-na-mao/feed-url`, 'POST');
    const createdAt = new Date().toISOString();
    setSettings((prev) => (prev ? { ...prev, cnm_feed_url_created_at: createdAt } : prev));
    return (res as { feed_url?: string } | null)?.feed_url ?? null;
  } catch {
    notifications.show({ title: t('portal.notifications.erroTituloErro'), message: t('chavesNaMao.erroFeed'), type: 'error' });
    return null;
  }
}

/**
 * Saves the agency's Chaves na Mão lead credentials (account e-mail and
 * "Token Integração de leads"); the token is stored hashed.
 *
 * @returns The webhook URL to send Chaves na Mão, or null on failure.
 */
async function doSaveCnmLeadCredentials(
  agencyId: string | undefined, t: (k: string) => string,
  setSettings: React.Dispatch<React.SetStateAction<PortalSettings | null>>,
  email: string, token: string,
): Promise<string | null> {
  if (!agencyId) return null;
  try {
    const res = await apiRequest(`/portal-integration/agencies/${agencyId}/settings/chaves-na-mao/leads`, 'POST', { email, token });
    const savedAt = new Date().toISOString();
    setSettings((prev) => (prev ? { ...prev, cnm_lead_email: email.trim().toLowerCase(), cnm_lead_saved_at: savedAt } : prev));
    return (res as { lead_url?: string } | null)?.lead_url ?? null;
  } catch {
    notifications.show({ title: t('portal.notifications.erroTituloErro'), message: t('chavesNaMao.erroLeads'), type: 'error' });
    return null;
  }
}

/**
 * Exposes memoized portal actions (update, connect, disconnect) for a component.
 *
 * @param deps - Portal action dependencies.
 * @returns The bound portal actions.
 */
export function usePortalActions(deps: Deps): PortalActions {
  const { agencyId, t, fetchSettings } = deps;

  const updateSettings = useCallback(
    (updates: Partial<PortalSettings>) => doUpdate(deps, updates),
    [agencyId, t, fetchSettings],
  );

  const connectOlx = useCallback(() => doConnect(agencyId, t), [agencyId, t]);

  const disconnectOlx = useCallback(() => doDisconnect(agencyId, t, fetchSettings), [agencyId, t, fetchSettings]);

  const confirmOlx = useCallback(() => doConfirmOlx(agencyId, t, fetchSettings), [agencyId, t, fetchSettings]);

  const checkOlxPlan = useCallback(() => doCheckOlxPlan(agencyId, t, fetchSettings), [agencyId, t, fetchSettings]);



  const createCnmFeedUrl = useCallback(
    () => doCreateCnmFeedUrl(agencyId, t, deps.setSettings), [agencyId, t, deps.setSettings],
  );

  const saveCnmLeadCredentials = useCallback(
    (email: string, token: string) => doSaveCnmLeadCredentials(agencyId, t, deps.setSettings, email, token),
    [agencyId, t, deps.setSettings],
  );

  return {
    updateSettings, connectOlx, disconnectOlx, confirmOlx, checkOlxPlan,
    createCnmFeedUrl, saveCnmLeadCredentials,
  };
}
